import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, Modal, QueryStatus, ScaledInput, useToast } from "@cx/ui";
import type { ContactListItem, ListResult, SellerView } from "@cx/contracts";
import { formatPct, toPct4, type CommissionBase } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { CommissionRulesBox } from "./CommissionRules";
import { COMMISSION_BASE_LABEL } from "./labels";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

/** Vendedores (RF-CAD-05): lista, novo vendedor e regras gerais de comissão. */
export function SellersPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");
  const term = q.trim();
  const params = new URLSearchParams({ ...(term ? { q: term } : {}), ...(status ? { status } : {}) });
  const list = useQuery({
    queryKey: ["sellers", companyId, term, status],
    queryFn: () => api<{ data: SellerView[] }>(`/sellers?${params}`),
  });
  const [creating, setCreating] = useState(false);
  const showUser = can(perms, "users", "read");
  const canCreate = can(perms, "sellers", "create") && can(perms, "contacts", "create");
  return (
    <>
      <ContentHeader
        title="Vendedores"
        breadcrumb={["Cadastros", "Vendedores"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Incluir vendedor
            </button>
          )
        }
      />
      <div className="box card mb-3">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-6">
            <label className="visually-hidden" htmlFor="sellers-q">
              Pesquisar vendedor
            </label>
            <input
              id="sellers-q"
              type="search"
              className="form-control"
              placeholder="Pesquisar por nome ou CPF/CNPJ"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="sellers-status">
              Situação
            </label>
            <select
              id="sellers-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="">Todas as situações</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && list.data.data.length === 0 && (
          <EmptyState
            title="Nenhum vendedor"
            description="Vendedores recebem comissão pelas vendas e podem ter acesso restrito à própria carteira."
            actions={canCreate ? [{ label: "Incluir vendedor", onClick: () => setCreating(true) }] : []}
          />
        )}
        {list.isSuccess && list.data.data.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Vendedores">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">E-mail</th>
                  <th scope="col" className="text-end">
                    Comissão padrão
                  </th>
                  <th scope="col">Base</th>
                  {showUser && <th scope="col">Usuário de acesso</th>}
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Link to="/cadastros/vendedores/$id" params={{ id: s.id }}>
                        {s.name}
                      </Link>
                    </td>
                    <td>{s.email ?? "—"}</td>
                    <td className="text-end">{formatPct(s.defaultCommissionPct4)}</td>
                    <td>{COMMISSION_BASE_LABEL[s.commissionBase]}</td>
                    {showUser && <td>{s.user ? s.user.name : "—"}</td>}
                    <td>{s.status === "active" ? "Ativo" : "Inativo"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CommissionRulesBox />
      {creating && (
        <NewSellerDialog
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            qc.invalidateQueries({ queryKey: ["sellers", companyId] });
            qc.invalidateQueries({ queryKey: ["contacts", companyId] });
            navigate({ to: "/cadastros/vendedores/$id", params: { id } });
          }}
        />
      )}
    </>
  );
}

/** Novo vendedor: contato existente (ganha o tipo "Vendedor") ou contato novo com os dados mínimos. */
function NewSellerDialog({ onClose, onSaved }: { onClose(): void; onSaved(id: string): void }) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [name, setName] = useState("");
  const [personType, setPersonType] = useState<"F" | "J">("F");
  const [document, setDocument] = useState("");
  const [email, setEmail] = useState("");
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [q, setQ] = useState("");
  const [pct, setPct] = useState<number | null>(0);
  const [base, setBase] = useState<CommissionBase>("invoiced");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const term = q.trim();
  const found = useQuery({
    queryKey: ["contacts", companyId, { q: term, picker: "seller" }],
    enabled: mode === "existing" && term.length >= 2,
    queryFn: () =>
      api<ListResult<ContactListItem>>(`/contacts?${new URLSearchParams({ q: term, limit: "8" })}`),
  });
  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/sellers", {
        method: "POST",
        body: JSON.stringify({
          ...(mode === "existing"
            ? { contactId: picked?.id }
            : { contact: { name, personType, document: document || null, email: email || null } }),
          defaultCommissionPct4: pct ?? 0,
          commissionBase: base,
        }),
      }),
    onSuccess: (r) => {
      toast("success", "Vendedor cadastrado");
      setOpen(false);
      onSaved(r.id);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && (e.code === "seller_exists" || e.code === "seller_deleted"))
        setErrors({ contactId: e.message });
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title="Novo vendedor"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || (mode === "existing" && !picked)}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <fieldset className="mb-3">
        <legend className="form-label fs-6">Contato do vendedor</legend>
        {(
          [
            ["new", "Cadastrar novo contato"],
            ["existing", "Usar contato existente"],
          ] as const
        ).map(([m, label]) => (
          <div key={m} className="form-check form-check-inline">
            <input
              id={`seller-mode-${m}`}
              type="radio"
              name="seller-mode"
              className="form-check-input"
              checked={mode === m}
              onChange={() => setMode(m)}
            />
            <label className="form-check-label" htmlFor={`seller-mode-${m}`}>
              {label}
            </label>
          </div>
        ))}
      </fieldset>
      {mode === "new" ? (
        <div className="row">
          <Field label="Nome" required className="col-12" error={errors["contact.name"]}>
            <input value={name} maxLength={120} data-autofocus onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Tipo da pessoa" className="col-md-4">
            <select
              className="form-select"
              value={personType}
              onChange={(e) => setPersonType(e.target.value as "F" | "J")}
            >
              <option value="F">Física</option>
              <option value="J">Jurídica</option>
            </select>
          </Field>
          <Field
            label={personType === "F" ? "CPF" : "CNPJ"}
            className="col-md-8"
            error={errors["contact.document"]}
          >
            <input value={document} onChange={(e) => setDocument(e.target.value)} />
          </Field>
          <Field label="E-mail" className="col-12" error={errors["contact.email"]}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>
      ) : (
        <div className="mb-3">
          {picked ? (
            <p data-testid="seller-contact">
              Contato: <strong>{picked.name}</strong>{" "}
              <button type="button" className="btn btn-sm btn-link" onClick={() => setPicked(null)}>
                Trocar
              </button>
            </p>
          ) : (
            <>
              <label className="form-label" htmlFor="seller-contact-search">
                Buscar contato
              </label>
              <input
                id="seller-contact-search"
                type="search"
                className="form-control"
                placeholder="Nome ou CPF/CNPJ"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {term.length >= 2 && (
                <ul className="list-group mt-1" aria-label="Contatos encontrados">
                  {found.isSuccess && found.data.data.length === 0 && (
                    <li className="list-group-item text-muted">Nenhum contato encontrado.</li>
                  )}
                  {found.data?.data.map((c) => (
                    <li key={c.id} className="list-group-item d-flex align-items-center gap-2">
                      <span className="flex-grow-1">{c.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        aria-label={`Escolher ${c.name}`}
                        onClick={() => setPicked({ id: c.id, name: c.name })}
                      >
                        Escolher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {errors.contactId && <div className="invalid-feedback d-block">{errors.contactId}</div>}
        </div>
      )}
      <div className="row">
        <Field label="Comissão padrão" className="col-md-6" error={errors.defaultCommissionPct4}>
          <ScaledInput value={pct} parse={toPct4} format={pctNumber} suffix="%" onChange={setPct} />
        </Field>
        <Field label="Base da comissão" className="col-md-6">
          <select
            className="form-select"
            value={base}
            onChange={(e) => setBase(e.target.value as CommissionBase)}
          >
            <option value="invoiced">{COMMISSION_BASE_LABEL.invoiced}</option>
            <option value="received">{COMMISSION_BASE_LABEL.received}</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}
